import { useEffect, useState } from "react";
import axios from "axios";
import { Reveal, Overline } from "@/components/site/Reveal";
import { motion } from "framer-motion";
import { IMAGES, API } from "@/lib/site";
import { COMMUNITY_PHOTOS } from "@/lib/communityPhotos";
import { Lightbox } from "@/components/site/Lightbox";
import { useLang } from "@/context/LanguageContext";

const Frame = ({ src, alt, className, testId, caption, position = "center", onClick }) => (
  <motion.div
    data-testid={testId}
    onClick={onClick}
    whileHover={{ y: -6 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    className={`relative overflow-hidden group cursor-pointer ${className}`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{ objectPosition: position }}
      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
    />
    <div className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/30" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    {caption && (
      <span className="absolute bottom-4 left-4 right-4 text-white text-sm font-medium opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
        {caption}
      </span>
    )}
  </motion.div>
);

const FBLA_EXTRA = ["/fbla20.jpg", "/fbla21.jpg", "/fbla22.jpg", "/fbla23.jpg"];
const ICDH_EVENTS = ["/icdh16.jpg", "/icdh17.jpg", "/icdh18.jpg"];
const INITIAL_COUNT = 12;

export const Galleries = () => {
  const { t } = useLang();
  const [uploaded, setUploaded] = useState({ fbla: [], icdh: [] });
  const [showAll, setShowAll] = useState(false);
  const [lb, setLb] = useState({ open: false, images: [], index: 0 });
  const shown = showAll ? COMMUNITY_PHOTOS : COMMUNITY_PHOTOS.slice(0, INITIAL_COUNT);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => {
      const grouped = { fbla: [], icdh: [] };
      r.data.forEach((g) => { if (grouped[g.category]) grouped[g.category].push(g); });
      setUploaded(grouped);
    }).catch(() => {});
  }, []);

  const fileUrl = (g) => `${API}/gallery/file/${g.storage_path}`;
  const fblaList = [IMAGES.fbla4, IMAGES.fbla1, IMAGES.fbla2, ...uploaded.fbla.map(fileUrl), ...FBLA_EXTRA];
  const icdhList = [IMAGES.fbla3, IMAGES.icdhStudents, "/icdh19.jpg", ...uploaded.icdh.map(fileUrl), ...ICDH_EVENTS, ...COMMUNITY_PHOTOS];

  const open = (list, src) => () =>
    setLb({ open: true, images: list, index: Math.max(0, list.indexOf(src)) });

  const UploadedStrip = ({ list, group, testId }) =>
    list.length ? (
      <Reveal delay={0.15}>
        <div data-testid={testId} className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {list.map((g) => (
            <Frame key={g.id} testId={`uploaded-${g.id}`} src={fileUrl(g)} alt={g.caption || "Gallery photo"}
              caption={g.caption} className="aspect-[4/3]" onClick={open(group, fileUrl(g))} />
          ))}
        </div>
      </Reveal>
    ) : null;

  return (
    <section id="galleries" data-testid="galleries-section" className="bg-[#F8F9FA] py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 space-y-28 md:space-y-40">
        {/* FBLA */}
        <div>
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <Overline>{t("galleries.fblaOverline")}</Overline>
                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0A1128]">{t("galleries.fblaTitle")}</h2>
              </div>
              <p className="max-w-md text-[#4B5563] md:text-lg leading-relaxed">{t("galleries.fblaSub")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <Frame testId="fbla-image-main" src={IMAGES.fbla4} alt="FBLA chapter with banner at ICDH"
                caption={t("galleries.fblaCaption")} className="md:col-span-8 aspect-[16/10]" position="center 25%" onClick={open(fblaList, IMAGES.fbla4)} />
              <div className="md:col-span-4 grid grid-rows-2 gap-6">
                <Frame testId="fbla-image-2" src={IMAGES.fbla1} alt="FBLA delegates at national conference"
                  caption="National conference" className="aspect-auto min-h-[180px]" position="center 30%" onClick={open(fblaList, IMAGES.fbla1)} />
                <Frame testId="fbla-image-3" src={IMAGES.fbla2} alt="FBLA members at ICDH"
                  caption="Chapter members" className="aspect-auto min-h-[180px]" position="center 25%" onClick={open(fblaList, IMAGES.fbla2)} />
              </div>
            </div>
          </Reveal>
          <UploadedStrip list={uploaded.fbla} group={fblaList} testId="fbla-uploaded" />
          <Reveal delay={0.15}>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="fbla-extra">
              {FBLA_EXTRA.map((src, i) => (
                <Frame key={i} testId={`fbla-extra-${i}`} src={src} alt="FBLA students at the National Leadership Conference"
                  className="aspect-[4/3]" onClick={open(fblaList, src)} />
              ))}
            </div>
          </Reveal>
        </div>

        {/* ICDH */}
        <div>
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <Overline>{t("galleries.icdhOverline")}</Overline>
                <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0A1128]">{t("galleries.icdhTitle")}</h2>
              </div>
              <p className="max-w-md text-[#4B5563] md:text-lg leading-relaxed">{t("galleries.icdhSub")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:auto-rows-[210px]">
              <Frame testId="icdh-image-main" src={IMAGES.fbla3} alt="ICDH students with FBLA banner"
                caption={t("galleries.icdhCaption1")} className="md:col-span-7 md:row-span-2" position="center 20%" onClick={open(icdhList, IMAGES.fbla3)} />
              <Frame testId="icdh-image-2" src={IMAGES.icdhStudents} alt="ICDH classroom in Haiti"
                caption={t("galleries.icdhCaption2")} className="md:col-span-5 md:row-span-1" onClick={open(icdhList, IMAGES.icdhStudents)} />
              <Frame testId="icdh-image-3" src="/icdh19.jpg" alt="ICDH awards ceremony"
                caption={t("galleries.icdhCaption2")} className="md:col-span-5 md:row-span-1" position="center 30%" onClick={open(icdhList, "/icdh19.jpg")} />
            </div>
          </Reveal>
          <UploadedStrip list={uploaded.icdh} group={icdhList} testId="icdh-uploaded" />

          <Reveal delay={0.12}>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="icdh-events">
              {ICDH_EVENTS.map((src, i) => (
                <Frame key={i} testId={`icdh-event-${i}`} src={src} alt="ICDH ceremony and campus"
                  className="aspect-[4/3]" onClick={open(icdhList, src)} />
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="community-gallery">
              {shown.map((src, i) => (
                <Frame key={i} testId={`community-photo-${i}`} src={src} alt="ICDH school life"
                  className="aspect-[4/3]" onClick={open(icdhList, src)} />
              ))}
            </div>
            {COMMUNITY_PHOTOS.length > INITIAL_COUNT && (
              <div className="mt-10 text-center">
                <button data-testid="community-toggle" onClick={() => setShowAll((v) => !v)}
                  className="px-8 py-3 border border-[#0A1128]/25 text-[#0A1128] font-medium hover:border-[#D4AF37] hover:text-[#B8901F] transition-colors">
                  {showAll ? t("galleries.showFewer") : t("galleries.viewAll").replace("{n}", COMMUNITY_PHOTOS.length)}
                </button>
              </div>
            )}
          </Reveal>
        </div>
      </div>

      <Lightbox
        open={lb.open}
        images={lb.images}
        index={lb.index}
        onClose={() => setLb((s) => ({ ...s, open: false }))}
        onNav={(i) => setLb((s) => ({ ...s, index: i }))}
      />
    </section>
  );
};
